import { ReactElement } from 'react';
import {
  ModelScene,
  // IndexScene,
  DeleteScene,
  UpdateScene,
  EntityScene,
  CreateScene,
  // ListFilter,
  useEntity,
  EntityFormActions,
  withHasPerm,
  IndexScene,
} from 'components';
import { Form } from './Form';
import { Nav } from './Nav';
import { actions, columns } from 'modules/invoicing/income';
import permissions from 'modules/invoicing/income/permissions';
import { IncomeBuilder, IncomeLinker } from 'modules/linker';
import { History, StateFilters } from 'modules/invoicing/common';
import type { Income } from 'modules/invoicing/income/types';
import { InvoiceTypeFilterChip } from 'modules/invoicing/InvoiceTypeFilterChip';
import { useInitialValues } from 'modules/invoicing/income/useInitialValues';
import { useLocation, useNavigate } from 'react-router';
import { ListExport } from 'components/ListActions';
import { useHasPerm } from 'core/permissions';
import { Button } from '@ff-it/ui';
import { Applicator } from 'modules/applicator/Applicator';
import AccountingSyncButton from 'modules/invoicing/common/AccountingSyncButton';
import { InvoiceStateFilterChip } from 'modules/invoicing/InvoiceStateFilterChip';
import { SearchFilterInput } from 'components/filters/SearchFilterInput';
import { SelectFilterChip } from 'components/filters/SelectFilterChip';
import { DateFilterChip } from 'components/filters/DateFilterChip';
import { CompanyFilterChip } from 'modules/core/company/components';
import { UserFilterChip } from 'components/filters/UserFilterChip';
import { ListTotalsFooter } from 'modules/invoicing/common/ListTotalsFooter';
export { columns };

const ProtectedIncomeLinker = withHasPerm(IncomeLinker, permissions.link);
const ProtectedIncomeBuilder = withHasPerm(IncomeBuilder, permissions.change);
const ProtectedApplicator = withHasPerm(Applicator, permissions.change);

function CreateInvoiceScene(): ReactElement {
  const initialValues = useInitialValues(useLocation().state?.creditSource);

  return (
    <CreateScene initialValues={initialValues}>
      <Form />
    </CreateScene>
  );
}

const model = {
  endpoint: 'invoicing/income/',
  title: 'Income',
  pluralTitle: 'Income',
  entityTitle: (e: Income) => e.number,
  permissions,
};

function InvoiceUpdateScene(): ReactElement {
  const { item } = useEntity();
  const isDraft = item.state === 'DRAFT';
  const navigate = useNavigate();
  // The income-anchored builder targets DRAFT campaign invoices only (not AVB bonus invoices).
  const canBuild = useHasPerm(permissions.change) && isDraft && item.type === 'INVOICE' && item.source_avb === null;
  // Entering the builder is non-destructive; the overwrite is confirmed at submit time (useRebuilder).
  return (
    <UpdateScene
      heading={null}
      actions={
        <>
          {canBuild && (
            <Button
              variant="secondary-danger"
              size="sm"
              testId="re-build"
              icon="arrow-rotate-left"
              className="ml-1"
              onClick={() => navigate('build')}
            >
              Re-build
            </Button>
          )}
          <EntityFormActions actions={actions} ml="auto" />
        </>
      }
      disabled={!isDraft}
    >
      <Form update />
    </UpdateScene>
  );
}

const sourceOptions = [
  { value: 'CAMPAIGN', label: 'Campaign' },
  { value: 'AVB', label: 'AVB' },
];

export const route = {
  path: 'income',
  element: <ModelScene {...model} />,
  children: [
    {
      index: true,
      element: (
        <IndexScene
          footer={<ListTotalsFooter />}
          columns={columns}
          toolbar={
            <>
              <AccountingSyncButton />
              <ListExport permission="invoicing.export_income" />
            </>
          }
          storageKey="invoices_income"
          filterFields={
            <>
              <SearchFilterInput />
              <SelectFilterChip name="source" options={sourceOptions} />
              <InvoiceTypeFilterChip />
              <InvoiceStateFilterChip />
              <CompanyFilterChip name="payer" />
              <DateFilterChip name="date_from" />
              <DateFilterChip name="date_to" />
              <UserFilterChip name="issued_by" label="Issuer" />
              <StateFilters />
            </>
          }
        />
      ),
    },
    {
      path: 'create',
      element: <CreateInvoiceScene />,
    },
    {
      path: ':id/',
      element: (
        <EntityScene>
          <Nav />
        </EntityScene>
      ),
      children: [
        {
          index: true,
          element: <InvoiceUpdateScene />,
        },
        {
          path: 'link',
          element: <ProtectedIncomeLinker />,
        },
        {
          path: 'build',
          element: <ProtectedIncomeBuilder />,
        },
        {
          path: 'application',
          element: <ProtectedApplicator />,
        },
        {
          path: 'delete',
          element: <DeleteScene />,
        },
        {
          path: 'history',
          element: <History />,
        },
      ],
    },
  ],
};
